/**
 * Level rubrics (S3, migration 214): each criterion shares the same levels, and a
 * level earns its exact share of the criterion's points in hundredths. The server
 * derives every baseline again; these helpers only say the same thing first.
 */
import type { CriterionLevel, RubricCriterion } from "@/lib/api/assignments";

export interface LevelAward { points: string; reason: string }

export interface LevelLine {
  criterion: RubricCriterion;
  levelId: string | null;
  levelName: string | null;
  baseline: number | null;
  awarded: number | null;
  adjusted: boolean;
  problem: string | null;
}

export const hasLevels = (rubric: RubricCriterion[] | null | undefined): boolean =>
  Boolean(rubric?.some(criterion => criterion.levels?.length));

/** A level's exact points: its share of the criterion's points, rounded half up to the hundredth. */
export function levelPoints(points: number, share: number): number {
  return Math.round(Math.round(points * 100) * share / 100) / 100;
}

export const pointsWord = (value: number) => String(Number(value.toFixed(2)));

const hundredths = (value: string) => /^\d+(\.\d{1,2})?$/.test(value.trim());

/** Each criterion's chosen level, baseline and award, with what is wrong at it. */
export function levelLines(rubric: RubricCriterion[], levels: Record<string, string>, awards: Record<string, LevelAward>): LevelLine[] {
  return rubric.map(criterion => {
    const level = criterion.levels?.find(item => item.id === levels[criterion.id]) ?? null;
    const baseline = level ? levelPoints(Number(criterion.points), level.share) : null;
    const award = awards[criterion.id];
    const typed = award?.points.trim() ?? "";
    let awarded = baseline, adjusted = false, problem: string | null = level ? null : `Choose a level for ${criterion.label}.`;
    if (level && typed) {
      const value = Number(typed);
      if (!hundredths(typed) || value > Number(criterion.points)) {
        problem = `Award ${criterion.label} 0 to ${pointsWord(Number(criterion.points))} points, to the hundredth.`;
        awarded = null;
      } else if (value !== baseline) {
        awarded = value;
        adjusted = true;
        if ((award?.reason.trim().length ?? 0) < 10) problem = `Say why ${criterion.label} earns ${pointsWord(value)} rather than ${level.name}'s ${pointsWord(baseline!)}.`;
      }
    }
    return { criterion, levelId: level?.id ?? null, levelName: level?.name ?? null, baseline, awarded, adjusted, problem };
  });
}

export function levelTotal(lines: LevelLine[]): number | null {
  return lines.every(line => line.awarded != null && !line.problem) ? Math.round(lines.reduce((sum, line) => sum + line.awarded! * 100, 0)) / 100 : null;
}

/** The levels and only the adjusted awards a mark sends. */
export function levelMark(lines: LevelLine[], awards: Record<string, LevelAward>) {
  return {
    levels: Object.fromEntries(lines.flatMap(line => line.levelId ? [[line.criterion.id, line.levelId]] : [])),
    awards: Object.fromEntries(lines.filter(line => line.adjusted).map(line => [line.criterion.id, { points: line.awarded!, reason: awards[line.criterion.id]!.reason.trim() }])),
  };
}

/** One criterion of a released or recorded level mark, in words. What earns a level stays in the brief. */
export interface LevelResult { criterionId: string; words: string }

/** "Use of sources: Secure, 0.9 of 1 (0.8 for Secure: Every claim is sourced.)" for each marked criterion. */
export function levelResults(rubric: RubricCriterion[], evidence: Record<string, CriterionLevel>): LevelResult[] {
  return rubric.flatMap(criterion => {
    const mark = evidence[criterion.id];
    if (!mark) return [];
    const name = criterion.levels?.find(level => level.id === mark.level_id)?.name ?? mark.level_id;
    const base = `${criterion.label}: ${name}, ${pointsWord(mark.awarded)} of ${pointsWord(Number(criterion.points))}`;
    return [{ criterionId: criterion.id, words: mark.reason ? `${base} (${pointsWord(mark.baseline)} for ${name}: ${mark.reason})` : base }];
  });
}

export const levelWords = (rubric: RubricCriterion[], evidence: Record<string, CriterionLevel>): string[] =>
  levelResults(rubric, evidence).map(result => result.words);
